import { Dispatch, FC, SetStateAction, useState } from "react";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { StageType } from "../../../../../../graphql/types";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import ReactHookDsmPicker from "../../../../../modules/Helpers/ReactHookDsmDatePicker";
import { DsmButton, DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { AnimalProductivity } from "../../../../../../graphql/generated/blonk/cattle";
import { Spacer } from "../../../helpers/Spacer";
import { FormType } from "../../common";
import { useFormContext } from "react-hook-form";
import { DairyV2Input } from "../../../../models/Intervention/DairyV2Intervention";
import RowSelectWithMetricsV2 from "../../../../../modules/Farms/Intervention/RowSelectWithMetricsV2";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import DefinitionsDialog from "../../CommonDataParts/v2.0/DefinitionsDialog";
import lookupValues from "../../../../helpers/lookupValues";
import EditModeButtons from "../../../helpers/EditModeButtons";

interface DairyV2InputProductivityComponentProp {
  stageType: StageType;
  fieldItemPrefix: string;
  formType: FormType;
  cattleProductivity: string;
  startDate: Date | string;
  isProductivityEdit: boolean;
  setIsProductivityEdit: Dispatch<SetStateAction<boolean>>;
  resetEdit: () => void;
}

const DairyV2InputProductivityComponent: FC<DairyV2InputProductivityComponentProp> = ({
  stageType,
  fieldItemPrefix,
  formType,
  cattleProductivity,
  startDate,
  isProductivityEdit,
  setIsProductivityEdit,
  resetEdit,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const fc = useFormContext<DairyV2Input>();

  const [openDescriptionDialog, setOpenDescriptionDialog] = useState<boolean>(false);

  const cattleProductivityOptions = () =>
    Object.entries(AnimalProductivity).map(([key, value]) => ({
      text: key,
      value,
    }));

  const renderStartDate = () => {
    return <ReactHookDsmPicker
      name={`${fieldItemPrefix}.originalStartDate`}
      label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.START_DATE' })}
      tooltip={intl.formatMessage({
        id: stageType === StageType.ContinuousGrowing ?
          'SUSTELL.STAGE.DAIRY.INPUT.START_DATE.CONTINUOUS_GROWING.TOOLTIP' :
          'SUSTELL.STAGE.DAIRY.INPUT.START_DATE.TOOLTIP'
      })}
      defaultValue={startDate?.toString()}
      disabled
    />
  }

  return (
    <>
      {openDescriptionDialog && (
        <DefinitionsDialog
          open
          prefix='SUSTELL.STAGE.DAIRY.INPUT.CATTLE_PRODUCTIVITY.DEFINITIONS.DAIRY_COW'
          itemHeader='SUSTELL.STAGE.DAIRY.INPUT.CATTLE_PRODUCTIVITY'
          items={lookupValues.cattleInputDefinitions}
          outroText={true}
          handleClose={() => setOpenDescriptionDialog(false)}
        />
      )}
      <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
        {!isProductivityEdit ? (
          <>
            {(isFieldFilled(fc.getValues(`${fieldItemPrefix}.cattleProductivity`))
              && fc.getValues(`${fieldItemPrefix}.cattleProductivity`) !== cattleProductivity) ? (
              <RowSelectWithMetricsV2
                name={`${fieldItemPrefix}.cattleProductivity`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.CATTLE_PRODUCTIVITY' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.CATTLE_PRODUCTIVITY.TOOLTIP' })}
                options={cattleProductivityOptions()}
                adornment=" "
                baseline={cattleProductivity}
                disabled
              />
            ) : (
              <ReactHookDsmSelect
                name={`${fieldItemPrefix}.originalCattleProductivity`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.CATTLE_PRODUCTIVITY' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.CATTLE_PRODUCTIVITY.TOOLTIP' })}
                options={cattleProductivityOptions()}
                defaultValue={cattleProductivity}
                disabled
              />
            )}
            {renderStartDate()}
          </>
        ) : (
          <>
            <div>
              <RowSelectWithMetricsV2
                name={`${fieldItemPrefix}.cattleProductivity`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.CATTLE_PRODUCTIVITY' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.CATTLE_PRODUCTIVITY.TOOLTIP' })}
                options={cattleProductivityOptions()}
                adornment=" "
                baseline={cattleProductivity}
                disabled={false}
                useStyle={false}
              />
              <Spacer />
            </div>

            {renderStartDate()}

            <DsmButton
              variant="text"
              onClick={() => setOpenDescriptionDialog(true)}
            >
              <DsmIcon slot="before" name="general/eye" />
              {intl.formatMessage({ id: 'DATABASE_FOUNDATION_DEFINITIONS' })}
            </DsmButton>
          </>
        )}
      </DsmGrid>
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isProductivityEdit}
          onEdit={() => setIsProductivityEdit(true)}
          onCancel={resetEdit}
        />}
    </>
  )
}

export default DairyV2InputProductivityComponent;
